import { useScenarioForm } from './contexts';
import { useStore } from '@tanstack/react-form';
import {
  DropdownField,
  NumberField,
  TanstackFieldWrapper,
} from 'components/form-fields-2';
import {
  Layer,
  Link,
  RadioButton,
  RadioButtonGroup,
  TextInput,
} from '@carbon/react';
import { Edit } from '@carbon/react/icons';
import { capitalize, isEqual } from 'lodash-es';
import {
  FormationMapping,
  MainStream,
  TypeWellAggregation,
  TypeWellConfigRow,
  TypeWellConfigResponse,
  TypeWellConfigsResponse,
  WaterForecastOption,
  WaterForecastSource,
} from './types';
import { defaultTypeWellConfig } from './default-values';
import {
  buildTypeWellConfigRows,
  getTypeWellConfigErrors,
  orderRowsLike,
  withWaterForecastOption,
  withWaterForecastSource,
} from './utils';
import { useConfigSwitch } from './hooks';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from 'components/accordion/accordion';
import sectionStyles from './config-sections.module.scss';
import styles from './type-well-profile-configs.module.scss';
import { useEffect, useMemo, useRef, useState } from 'react';
import { atom, useAtomValue, useSetAtom } from 'jotai';
import { dcaScenariosListAtom, fetchDcaScenariosAtom } from './atoms';
import { DcaScenarioSelectionModal } from './dca-scenario-selection-modal';
import { TypeWellProfileSkeleton } from './section-skeletons';
import { createResourceAtom } from 'lib/atoms';

const FALLBACK_ATOM = atom(null);

const MAIN_STREAM_OPTIONS: MainStream[] = ['oil', 'gas', 'water'];
const AGGREGATION_OPTIONS: TypeWellAggregation[] = [
  'p10',
  'p50',
  'p90',
  'arithmetic_mean',
  'geometric_mean',
];
/** The choices for the Water Forecast Option radio buttons. */
const WATER_FORECAST_OPTIONS: WaterForecastOption[] = ['dca', 'ratio'];

/** The choices for the Water Forecast Source dropdown. */
const WATER_FORECAST_SOURCE_OPTIONS: WaterForecastSource[] = ['oil', 'gas'];

/** The TC Formation shown on the Fallback row. */
const FALLBACK_LABEL = 'Fallback';

/**
 * Returns a key for a row that stays the same when rows move, which happens
 * when formations are added or removed.
 * @param {TypeWellConfigRow} row - The row
 * @return {string} The row's key
 */
const rowKey = (row: TypeWellConfigRow): string => {
  if (row.id) return row.id;
  if (row.isFallback) return FALLBACK_LABEL;
  return `new-${row.tcFormationName}`;
};

const aggregationLabel = (value: string | null) => {
  if (!value) return '';
  if (value.startsWith('p')) return value.toUpperCase();
  return value
    .split('_')
    .map((word) => capitalize(word))
    .join(' ');
};

/**
 * Reads a decimal that the API sends as a string.
 * @param {string | null} value - The value from the API
 * @return {number | null} The number, or null when it is missing
 */
const parseDecimal = (value: string | null): number | null => {
  if (value === null || value === '') return null;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
};

/**
 * Turns a Type Well Profile Config from the API into a form row.
 * @param {TypeWellConfigResponse} resp - The config from the API
 * @return {TypeWellConfigRow} The form row
 */
const mapResponseToTypeWellConfig = (
  resp: TypeWellConfigResponse
): TypeWellConfigRow => ({
  id: String(resp.id),
  isFallback: resp.isFallback,
  tcFormationName: resp.typeCurveFormation,
  mainStream: resp.mainStream as MainStream,
  typeWellAggregation: resp.aggregation as TypeWellAggregation,
  dcaScenarioId: resp.dcaScenario ? String(resp.dcaScenario.id) : null,
  dcaScenarioName: resp.dcaScenario?.name ?? null,
  gasShrinkPct: parseDecimal(resp.gasShrinkPercentage),
  nglYield: parseDecimal(resp.nglYield),
  waterForecastOption: resp.waterForecastOption as WaterForecastOption,
  waterForecastSource: resp.waterForecastSource as WaterForecastSource | null,
  watercutPct: parseDecimal(resp.watercutPct),
  wgr: parseDecimal(resp.waterGasRatioInBblPerMmcf),
});

/**
 * The Type Well Profile Configs section of an area config. Its rows follow
 * the Formations table: one row per Type Curve Formation, plus a Fallback
 * row that is always last. Rows cannot be added or removed by hand.
 * @return {JSX.Element} The section
 */
export const TypeWellProfileConfigs = () => {
  const {
    form,
    selectedAreaConfig,
    setFieldBaseline,
    readOnly,
    formationMappingsReadyFor,
  } = useScenarioForm();
  const fetchDcaScenarios = useSetAtom(fetchDcaScenariosAtom);
  const dcaScenariosList = useAtomValue(dcaScenariosListAtom);
  const [dcaDrawerOpen, setDcaDrawerOpen] = useState(false);
  const [dcaEditIndex, setDcaEditIndex] = useState<number | null>(null);

  // Fetch existing type well configs from API
  const typeWellConfigsHref =
    selectedAreaConfig?.resources?.typeWellConfigs?.href || null;

  const typeWellConfigsAtom = useMemo(() => {
    if (!typeWellConfigsHref) return FALLBACK_ATOM;
    return createResourceAtom<TypeWellConfigsResponse>(typeWellConfigsHref);
  }, [typeWellConfigsHref]);

  const typeWellConfigsData = useAtomValue(typeWellConfigsAtom);

  const initializedForConfigRef = useConfigSwitch(selectedAreaConfig);

  useEffect(() => {
    fetchDcaScenarios();
  }, [fetchDcaScenarios]);

  // When API data loads, override with fetched values
  useEffect(() => {
    const configId = selectedAreaConfig?.id ?? null;
    if (initializedForConfigRef.current === configId) return;
    if (!typeWellConfigsData?.data || !('results' in typeWellConfigsData.data))
      return;
    initializedForConfigRef.current = configId;
    const results = typeWellConfigsData.data.results;
    const configs = results.map(mapResponseToTypeWellConfig);
    form.setFieldValue('typeWellConfigs', configs);
    setFieldBaseline('typeWellConfigs', configs);
  }, [
    typeWellConfigsData,
    form,
    setFieldBaseline,
    selectedAreaConfig?.id,
    initializedForConfigRef,
  ]);

  const pdpScenario = useMemo(
    () =>
      dcaScenariosList?.data?.find(
        (s) => s.name.toLowerCase() === 'pdp default'
      ) ?? null,
    [dcaScenariosList]
  );

  // Fill in the PDP Default DCA scenario on unsaved rows that have none,
  // which happens when rows are added before the DCA scenario list loads.
  // A new area config's defaults become its baseline, so clicking Add does
  // not show unsaved changes.
  useEffect(() => {
    if (!pdpScenario) return;

    const configs = form.state.values.typeWellConfigs;
    const isMissingDca = (c: TypeWellConfigRow) =>
      c.id === null && c.dcaScenarioId === null;
    if (!configs.some(isMissingDca)) return;

    const updated = configs.map((c) => {
      if (!isMissingDca(c)) return c;
      return {
        ...c,
        dcaScenarioId: String(pdpScenario.id),
        dcaScenarioName: pdpScenario.name,
      };
    });
    if (selectedAreaConfig?.id === 'local') {
      setFieldBaseline('typeWellConfigs', updated);
    } else {
      form.setFieldValue('typeWellConfigs', updated);
    }
    // form and setFieldBaseline have stable identities (useForm result and
    // useCallback). Including them would re-fire this hydrate on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdpScenario, selectedAreaConfig?.id]);

  // These values only re-run the row sync below. The sync reads form.state
  // instead, because another effect may have changed the values since this
  // render.
  const formationMappings = useStore(
    form.store,
    (state) => state.values.formationMappings
  );
  const typeWellConfigs = useStore(
    form.store,
    (state) => state.values.typeWellConfigs
  );

  // The Formations table as of the last row sync, used to find renamed
  // formations.
  const previousMappingsRef = useRef<{
    configId: string | null;
    mappings: FormationMapping[];
  }>({ configId: null, mappings: [] });

  // Keep one row per Type Curve Formation in the Formations table, plus the
  // Fallback row.
  useEffect(() => {
    const configId = selectedAreaConfig?.id ?? null;
    // Wait until both the Formations table and these rows hold this config's
    // data. Building rows from a table that is still loading would drop saved
    // rows.
    const isReady =
      !!configId &&
      formationMappingsReadyFor === configId &&
      (!typeWellConfigsHref || initializedForConfigRef.current === configId);
    if (!isReady) {
      previousMappingsRef.current = { configId: null, mappings: [] };
      return;
    }

    const { formationMappings: mappings, typeWellConfigs: rows } =
      form.state.values;
    const previous = previousMappingsRef.current;
    const isFirstSync = previous.configId !== configId;
    previousMappingsRef.current = { configId, mappings };

    const next = buildTypeWellConfigRows({
      rows,
      // The first sync after loading has nothing to compare against.
      previousMappings: isFirstSync ? mappings : previous.mappings,
      mappings,
      newRow: {
        ...defaultTypeWellConfig,
        dcaScenarioId: pdpScenario ? String(pdpScenario.id) : null,
        dcaScenarioName: pdpScenario?.name ?? null,
      },
    });

    // Saved rows load in id order, but the rows built above follow the
    // Formations table. Put the saved rows in table order too, so the new
    // order alone does not count as an unsaved change.
    let current = rows;
    if (isFirstSync && typeWellConfigsHref) {
      current = orderRowsLike(rows, next);
      if (!isEqual(current, rows)) setFieldBaseline('typeWellConfigs', current);
    }
    if (!isEqual(next, current)) form.setFieldValue('typeWellConfigs', next);
  }, [
    form,
    setFieldBaseline,
    formationMappings,
    typeWellConfigs,
    formationMappingsReadyFor,
    selectedAreaConfig?.id,
    typeWellConfigsHref,
    pdpScenario,
    initializedForConfigRef,
  ]);

  return (
    <>
      <Accordion defaultExpanded>
        <AccordionSummary className={sectionStyles.accordionSummaryBorder}>
          <h3 className={sectionStyles.sectionTitle}>
            Type Well Profile Configs
          </h3>
        </AccordionSummary>
        <AccordionDetails>
          <div className={sectionStyles.sectionDetails}>
            <p className={styles.description}>
              Configure type well profile parameters including stream selection
              and aggregation method.
            </p>

            {typeWellConfigsData?.loading ? (
              <TypeWellProfileSkeleton />
            ) : (
              <form.Subscribe
                selector={(state) => state.values.typeWellConfigs}
              >
                {(configs) => (
                  <>
                    {!configs.some((config) => !config.isFallback) && (
                      <p className={styles.emptyMessage}>
                        No Type Curve Formations are mapped yet. Map them in the
                        Formations table to add a config for each one.
                      </p>
                    )}
                    <div className={styles.rows}>
                      {configs.map((config, index) => (
                        <TypeWellConfigRowComponent
                          key={rowKey(config)}
                          index={index}
                          config={config}
                          form={form}
                          readOnly={readOnly}
                          onEditDca={(idx) => {
                            setDcaEditIndex(idx);
                            setDcaDrawerOpen(true);
                          }}
                        />
                      ))}
                    </div>
                  </>
                )}
              </form.Subscribe>
            )}
          </div>
        </AccordionDetails>
      </Accordion>

      <DcaScenarioSelectionModal
        open={dcaDrawerOpen}
        onClose={() => {
          setDcaDrawerOpen(false);
          setDcaEditIndex(null);
        }}
        currentDcaScenarioId={
          dcaEditIndex !== null
            ? form.state.values.typeWellConfigs[dcaEditIndex]?.dcaScenarioId
            : null
        }
        onSelect={(scenario) => {
          if (dcaEditIndex === null) return;
          const configs = [...form.state.values.typeWellConfigs];
          configs[dcaEditIndex] = {
            ...configs[dcaEditIndex],
            dcaScenarioId: String(scenario.id),
            dcaScenarioName: scenario.name,
          };
          form.setFieldValue('typeWellConfigs', configs);
        }}
      />
    </>
  );
};

interface TypeWellConfigRowComponentProps {
  index: number;
  config: TypeWellConfigRow;
  form: any;
  readOnly: boolean;
  onEditDca: (index: number) => void;
}

/**
 * One Type Well Profile Config row. Every row has the same columns, and a
 * Ratio row also shows its Water Forecast Source and ratio input.
 * @param {TypeWellConfigRowComponentProps} props - The row and its index
 * @return {JSX.Element} The row
 */
const TypeWellConfigRowComponent = ({
  index,
  config,
  form,
  readOnly,
  onEditDca,
}: TypeWellConfigRowComponentProps) => {
  const errors = getTypeWellConfigErrors(config);
  const isRatio = config.waterForecastOption === 'ratio';

  /**
   * Replaces this row with a changed copy.
   * @param {TypeWellConfigRow} row - The changed row
   */
  const setRow = (row: TypeWellConfigRow) => {
    const configs = [...form.state.values.typeWellConfigs];
    configs[index] = row;
    form.setFieldValue('typeWellConfigs', configs);
  };

  return (
    <div className={styles.configRow}>
      {/* TC Formation */}
      <div className={styles.tcFormationCell}>
        <Layer>
          <TextInput
            id={`twpFormation-${index}`}
            labelText="TC Formation"
            value={
              config.isFallback
                ? FALLBACK_LABEL
                : (config.tcFormationName ?? '')
            }
            readOnly
          />
        </Layer>
      </div>

      {/* Main Stream */}
      <div className={styles.mainStreamCell}>
        <form.Field name={`typeWellConfigs[${index}].mainStream` as any}>
          {(field: any) => (
            <TanstackFieldWrapper<MainStream> field={field}>
              {(props) => (
                <DropdownField
                  id={`twpMainStream-${index}`}
                  label="Select stream"
                  titleText="Main Stream"
                  // Lets the menu show outside the rows' scroll container
                  // instead of being cut off.
                  autoAlign
                  items={MAIN_STREAM_OPTIONS}
                  itemToString={(item: string | null) => capitalize(item ?? '')}
                  disabled={readOnly}
                  {...props}
                />
              )}
            </TanstackFieldWrapper>
          )}
        </form.Field>
      </div>

      {/* Type Well Aggregation */}
      <div className={styles.aggregationCell}>
        <form.Field
          name={`typeWellConfigs[${index}].typeWellAggregation` as any}
        >
          {(field: any) => (
            <TanstackFieldWrapper<TypeWellAggregation> field={field}>
              {(props) => (
                <DropdownField
                  id={`twpAggregation-${index}`}
                  label="Select aggregation"
                  titleText="Type Well Aggregation"
                  autoAlign
                  items={AGGREGATION_OPTIONS}
                  itemToString={(item: string | null) => aggregationLabel(item)}
                  disabled={readOnly}
                  {...props}
                />
              )}
            </TanstackFieldWrapper>
          )}
        </form.Field>
      </div>

      {/* DCA Scenario */}
      <div className={`${styles.labeledCell} ${styles.dcaScenarioCell}`}>
        <span className={styles.cellLabel}>DCA Scenario</span>
        <div className={styles.dcaScenarioLink}>
          {config.dcaScenarioName ? (
            <>
              <Link
                href={
                  config.dcaScenarioId
                    ? `/dca_scenarios/${config.dcaScenarioId}`
                    : undefined
                }
                target="_blank"
              >
                {config.dcaScenarioName}
              </Link>
              {!readOnly && (
                <button
                  type="button"
                  className={styles.editIconButton}
                  onClick={() => onEditDca(index)}
                >
                  <Edit size={16} />
                </button>
              )}
            </>
          ) : !readOnly ? (
            <button
              type="button"
              className={styles.editIconButton}
              onClick={() => onEditDca(index)}
            >
              <Edit size={16} />
              <span>Select</span>
            </button>
          ) : (
            <span className={styles.dcaScenarioEmpty}>--</span>
          )}
        </div>
      </div>

      {/* Gas Shrink Factor (%) */}
      <div className={styles.gasShrinkCell}>
        <form.Field name={`typeWellConfigs[${index}].gasShrinkPct` as any}>
          {(field: any) => (
            <TanstackFieldWrapper<number | null> field={field}>
              {(props) => (
                <NumberField
                  id={`twpGasShrink-${index}`}
                  label="Gas Shrink Factor (%)"
                  disabled={readOnly}
                  allowNegative={false}
                  decimalScale={2}
                  {...props}
                  invalid={!!errors.gasShrinkPct}
                  invalidText={errors.gasShrinkPct}
                />
              )}
            </TanstackFieldWrapper>
          )}
        </form.Field>
      </div>

      {/* NGL Yield (bbl/MMcf) */}
      <div className={styles.nglYieldCell}>
        <form.Field name={`typeWellConfigs[${index}].nglYield` as any}>
          {(field: any) => (
            <TanstackFieldWrapper<number | null> field={field}>
              {(props) => (
                <NumberField
                  id={`twpNglYield-${index}`}
                  label="NGL Yield (bbl/MMcf)"
                  disabled={readOnly}
                  allowNegative={false}
                  decimalScale={2}
                  {...props}
                  invalid={!!errors.nglYield}
                  invalidText={errors.nglYield}
                />
              )}
            </TanstackFieldWrapper>
          )}
        </form.Field>
      </div>

      {/* Water Forecast Option */}
      <div className={`${styles.labeledCell} ${styles.waterForecastCell}`}>
        <span className={styles.cellLabel}>Water Forecast Option</span>
        <div className={styles.radioGroup}>
          <RadioButtonGroup
            legendText=""
            // Use the row's key, not its index. When rows move, two groups
            // could share a name for a moment, and the browser would then
            // clear the selection in one of them.
            name={`water-forecast-${rowKey(config)}`}
            valueSelected={config.waterForecastOption}
            onChange={(value: string | number | undefined) =>
              setRow(
                withWaterForecastOption(config, value as WaterForecastOption)
              )
            }
            disabled={readOnly}
          >
            {WATER_FORECAST_OPTIONS.map((opt) => (
              <RadioButton
                key={opt}
                id={`water-forecast-${index}-${opt}`}
                labelText={opt === 'dca' ? 'DCA' : 'Ratio'}
                value={opt}
              />
            ))}
          </RadioButtonGroup>
        </div>
      </div>

      {/* Water Forecast Source and its ratio input. Every row keeps both
          cells so the columns line up, but only a Ratio row fills them. */}
      <div className={styles.waterRatioCell}>
        {isRatio && (
          <DropdownField<WaterForecastSource | null>
            id={`twpWaterSource-${index}`}
            label="Select source"
            titleText="Water Forecast Source"
            autoAlign
            items={WATER_FORECAST_SOURCE_OPTIONS}
            itemToString={(item: string | null) => capitalize(item ?? '')}
            value={config.waterForecastSource}
            onChange={(source) => {
              if (source) setRow(withWaterForecastSource(config, source));
            }}
            disabled={readOnly}
            invalid={!!errors.waterForecastSource}
            invalidText={errors.waterForecastSource}
          />
        )}
      </div>

      <div className={styles.waterRatioCell}>
        {isRatio &&
          (config.waterForecastSource === 'gas' ? (
            <form.Field key="wgr" name={`typeWellConfigs[${index}].wgr` as any}>
              {(field: any) => (
                <TanstackFieldWrapper<number | null> field={field}>
                  {(props) => (
                    <NumberField
                      id={`twpWgr-${index}`}
                      label="WGR (bbl/MMcf)"
                      disabled={readOnly}
                      allowNegative={false}
                      decimalScale={2}
                      {...props}
                      invalid={!!errors.wgr}
                      invalidText={errors.wgr}
                    />
                  )}
                </TanstackFieldWrapper>
              )}
            </form.Field>
          ) : (
            <form.Field
              key="watercut"
              name={`typeWellConfigs[${index}].watercutPct` as any}
            >
              {(field: any) => (
                <TanstackFieldWrapper<number | null> field={field}>
                  {(props) => (
                    <NumberField
                      id={`twpWatercut-${index}`}
                      label="Watercut (%)"
                      disabled={readOnly}
                      allowNegative={false}
                      decimalScale={2}
                      {...props}
                      invalid={!!errors.watercutPct}
                      invalidText={errors.watercutPct}
                    />
                  )}
                </TanstackFieldWrapper>
              )}
            </form.Field>
          ))}
      </div>
    </div>
  );
};
